import axios from 'axios';

/** Цвета бейджа роли - как в конструкторе ролей (roles.badge_*) */
export interface RoleBadgeColors {
  bg: string;
  text: string;
  border: string;
}

/** Роль текущей сессии для шапки: название и цвет бейджа */
export interface SessionRoleBadge {
  slug: string;
  name: string;
  badge: RoleBadgeColors;
}

const CACHE_KEY = 'roleBadge';

function hexToRgb(hex: string): [number, number, number] | null {
  const clean = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16)
  ];
}

function relativeLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const s = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2];
}

/**
 * Насыщенный цвет роли для полоски: более темный из фона и текста бейджа.
 * У светлых пресетов это цвет текста, у «Темного» и темных своих цветов - фон.
 */
export function roleAccentColor(badge: RoleBadgeColors): string {
  const bgLum = relativeLuminance(badge.bg);
  const textLum = relativeLuminance(badge.text);
  if (bgLum === null) return badge.text;
  if (textLum === null) return badge.bg;
  return bgLum < textLum ? badge.bg : badge.text;
}

/** Роль из кэша - только если она совпадает с ролью текущей сессии */
export function readCachedRoleBadge(slug: string): SessionRoleBadge | null {
  if (!slug) return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const cached = raw ? JSON.parse(raw) as SessionRoleBadge : null;
    return cached && cached.slug === slug && cached.badge ? cached : null;
  } catch {
    return null;
  }
}

/** Название и цвет роли сессии с сервера (тот же запрос, что и права роли); результат кэшируется */
export async function fetchRoleBadge(slug: string): Promise<SessionRoleBadge | null> {
  const res = await axios.get(`/api/v2/roles/${encodeURIComponent(slug)}`);
  const role = res.data?.success ? res.data.role : null;
  if (!role?.badge) return null;
  const result: SessionRoleBadge = { slug, name: role.name || slug, badge: role.badge };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch { /* кэш необязателен */ }
  return result;
}
